"use client";

import { useEffect, useState } from "react";
import UserMenu from "@/components/auth/UserMenu";
import StopImpersonationButton from "@/components/auth/StopImpersonationButton";
import { TenantRow } from "@/lib/types";
import { User } from "@supabase/supabase-js";
import Link from "next/link";
import { useImpersonation } from "@/hooks/useImpersonation";

interface NavbarClientProps {
  tenant: TenantRow;
  user: User | null;
  role: "admin" | "member" | null;
  memberType?: "owner" | "board-member" | "renter" | "property-manager" | "portal-admin" | null;
  impersonationInfo?: {
    isImpersonating: boolean;
    effectiveMemberType?: any;
  };
  hasAdminSidebar?: boolean;
}

export default function NavbarClient({ tenant, user, role, memberType, impersonationInfo, hasAdminSidebar }: NavbarClientProps) {
  const [isClient, setIsClient] = useState(false);
  const { impersonationState } = useImpersonation();

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Determine if the actual user is admin for showing admin-only controls in menu
  const isActuallyAdmin = role === "admin";

  return (
    <header className="w-full bg-white/60 backdrop-blur-sm shadow sticky top-0 z-50">
      <div className="flex">
        {/* Spacer for admin sidebar when present */}
        {hasAdminSidebar && <div className="w-16 flex-shrink-0" />}
        
        {/* Main header content container - matches main content layout */}
        <div className="flex-1 py-4">
          <div className="max-w-7xl pl-8 pr-8 flex items-center justify-between">
            {/* Left-aligned logo/brand */}
            <a href="/" className="flex items-center gap-2 hover:opacity-80">
              {tenant.logo_url ? (
                <img src={tenant.logo_url} alt="Logo" className="h-6 w-6 object-contain" />
              ) : (
                <img src="/logo.png" alt="Logo" className="h-6 w-6" />
              )}
              <span className="text-lg font-semibold text-gray-800">
                {tenant.name}
              </span>
            </a>
            
            {/* Right-aligned user menu - constrained within max-w-7xl container */}
            <div className="flex items-center gap-4">
              {isClient && impersonationState.isImpersonating && (
                <StopImpersonationButton 
                  effectiveRole={impersonationState.impersonatedRole || "public"} 
                  impersonationInfo={{
                    isImpersonating: impersonationState.isImpersonating,
                    actualMemberType: undefined,
                    effectiveMemberType: impersonationState.impersonatedMemberType,
                  }}
                />
              )}
              {isClient && (user?.email || impersonationState.isImpersonating) ? (
                <UserMenu 
                  userEmail={user?.email || "Admin"}
                  tenantId={tenant.id}
                  isActuallyAdmin={isActuallyAdmin}
                  memberType={memberType}
                />
              ) : (
                <Link
                  href="/auth/login"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
} 